import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { History, Undo2, Loader2, FileSpreadsheet, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

// Lists the most recent product CSV imports and lets the user undo
// any that haven't already been undone. Backed by:
//   GET  /api/products/import-batches
//   POST /api/products/import-batches/{batch_id}/undo
export default function ImportHistoryDialog({ open, onOpenChange, onChange }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [undoingId, setUndoingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/products/import-batches");
      setBatches(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load import history");
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const undo = async (batch_id) => {
    if (!window.confirm("Undo this import? Newly added products will be deleted and overwritten ones restored.")) return;
    setUndoingId(batch_id);
    try {
      const res = await api.post(`/products/import-batches/${batch_id}/undo`);
      const { deleted = 0, restored = 0 } = res.data || {};
      toast.success(`Import reverted — ${deleted} deleted, ${restored} restored`);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't undo import");
    }
    setUndoingId(null);
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="import-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-xl tracking-tight flex items-center gap-2">
            <History className="w-4 h-4 text-slate-400" /> Recent imports
          </DialogTitle>
          <p className="text-xs text-slate-500 font-['Manrope'] mt-0.5">
            Undo any product CSV import from the last 20 uploads. Undoing deletes the products it created and restores the ones it overwrote.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : batches.length === 0 ? (
          <div className="py-12 flex flex-col items-center justify-center text-center text-slate-400">
            <FileSpreadsheet className="w-8 h-8 mb-2" strokeWidth={1.5} />
            <p className="text-sm font-['Manrope']">No imports yet.</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1" data-testid="import-history-list">
            {batches.map((b) => (
              <div key={b.batch_id}
                data-testid={`import-history-row-${b.batch_id}`}
                className={`flex items-center gap-3 border rounded-xl p-3 ${b.undone ? "border-slate-100 bg-slate-50/60" : "border-slate-200 bg-white"}`}>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${b.undone ? "bg-slate-200/80" : "bg-rose-50"}`}>
                  {b.undone
                    ? <CheckCircle2 className="w-4 h-4 text-slate-400" />
                    : <FileSpreadsheet className="w-4 h-4 text-rose-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 font-['Outfit'] truncate">
                    {b.created_count} new · {b.updated_count} updated{b.skipped_count ? ` · ${b.skipped_count} skipped` : ""}
                  </p>
                  <p className="text-[11px] text-slate-500 font-['Manrope']">
                    {fmt(b.created_at)} · by {b.created_by_name || "Staff"} · {b.total_rows} rows
                  </p>
                </div>
                {b.undone ? (
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-['Manrope'] px-2">Undone</span>
                ) : (
                  <Button onClick={() => undo(b.batch_id)} disabled={undoingId === b.batch_id}
                    data-testid={`undo-batch-${b.batch_id}`}
                    variant="outline" size="sm"
                    className="border-rose-200 text-rose-600 hover:bg-rose-50 rounded-full h-8 px-3 text-xs">
                    {undoingId === b.batch_id ? (
                      <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Undoing…</>
                    ) : (
                      <><Undo2 className="w-3 h-3 mr-1" />Undo</>
                    )}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-end pt-3 border-t border-slate-100">
          <Button onClick={() => onOpenChange(false)} className="rounded-full h-10 px-5 text-sm bg-slate-900 text-white hover:bg-slate-800" data-testid="import-history-close">
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
